const VALID = /^[a-z][a-z0-9-]{1,62}[a-z0-9]$/;
const RESERVED = new Set(["app", "www"]);
export const ADMIN_SUBDOMAIN = "admin";
const HOST_PATTERN = /^([a-z0-9-]+)\.(gestionalo\.uy|gestionalo\.local|localhost)$/;

/**
 * Per-environment deploy hosts (app-dev, admin-staging, …) are the SAME surface
 * as their production counterpart, not enterprise tenants. Without this, a host
 * like app-dev.gestionalo.uy parses as the tenant slug "app-dev", the tenant
 * lookup 404s and EnterpriseNotFoundRedirect bounces the browser to production.
 *
 * The suffix is only stripped when the base is a platform-owned host, so a real
 * tenant named e.g. "acme-dev" keeps its own identity.
 */
const ENV_SUFFIX = /-(dev|staging|preview|test)$/;
const PLATFORM_HOSTS = new Set([...RESERVED, ADMIN_SUBDOMAIN]);

function normalizeEnvHost(sub: string): string {
  const base = sub.replace(ENV_SUFFIX, "");
  return base !== sub && PLATFORM_HOSTS.has(base) ? base : sub;
}

export function resolveSubdomainFromHostname(hostname: string): string | null {
  const host = hostname.toLowerCase().split(":")[0];
  const match = host.match(HOST_PATTERN);
  if (!match) return null;
  const sub = normalizeEnvHost(match[1]);
  if (RESERVED.has(sub)) return null;
  return VALID.test(sub) ? sub : null;
}

/**
 * Builds an absolute URL for an enterprise subdomain based on the current
 * browser origin.  Handles localhost, gestionalo.uy, gestionalo.local, etc.
 */
export function buildEnterpriseUrl(subdomain: string, path = "/"): string {
  const { protocol, host } = window.location;
  const hostWithoutPort = host.split(":")[0];
  const port = host.includes(":") ? `:${host.split(":")[1]}` : "";

  if (hostWithoutPort === "localhost") {
    return `${protocol}//${subdomain}.localhost${port}${path}`;
  }

  const parts = hostWithoutPort.split(".");
  const baseDomain = parts.length >= 2
    ? parts.slice(-2).join(".")
    : hostWithoutPort;

  return `${protocol}//${subdomain}.${baseDomain}${port}${path}`;
}
